import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import { not, or } from 'ember-truth-helpers';
import {
  LANGUAGE_LABELS,
  LANGUAGES,
} from 'frontend-utils/utils/constants/constant-values';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';
import { isPossiblePhoneNumber } from 'frontend-utils/utils/phone-number-utils';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import LogoField from 'lumen/components/settings/my-hotel/logo-field';
import hasUnifiedSettingsActiveFeature from 'lumen/helpers/has-unified-settings-active-feature';
import type { SubscriptionProfile } from 'lumen/models';
import type { SubscriptionProfileOwnedAttrs } from 'lumen/models/subscription/profile';
import type AjaxService from 'lumen/services/ajax';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { GENERAL_PROPERTY_FEATURES } from 'lumen/utils/constants/unified-settings-feature-constants';

const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
/**
 * Matches blocks of spaces/semicolons to replace with a single ';',
 * but explicitly ignores exactly "; " to preserve user formatting.
 * (?<![\s;])      : Start match only at the beginning of a block.
 * (?!; (?![\s;])) : Abort match if the block is exactly "; ".
 * [\s;]+          : Match the 1+ spaces/semicolons.
 */
const MULTI_EMAIL_REGEX = /(?<![\s;])(?!; (?![\s;]))[\s;]+/g;

type EditableField = 'brandUrl' | 'hotelName' | 'telnr';

// UNIFIED SETTINGS TODO: [INDU-1352] Discuss with the team to move this code to its own file,
//                        and terminate duplication with apps/lumen/app/components/settings/personal/forms/form-user-edit.gts
type Language = {
  id: string;
  label: string;
};

const DEFAULT_LANGUAGE: Language = {
  id: '',
  label: '',
};

type StardekkLanguageCode =
  | typeof LANGUAGES.NL
  | typeof LANGUAGES.FR
  | typeof LANGUAGES.EN
  | typeof LANGUAGES.DE
  | typeof LANGUAGES.ES
  | typeof LANGUAGES.IT
  | typeof LANGUAGES.PT_BR
  | 'pt'
  | 'el'
  | 'sv'
  | 'ru'
  | 'sl'
  | 'lt'
  | 'tr'
  | 'zh'
  | 'hr';

const STARDEKK_LANGUAGE_LABELS = {
  [LANGUAGES.NL]: LANGUAGE_LABELS[LANGUAGES.NL],
  [LANGUAGES.FR]: LANGUAGE_LABELS[LANGUAGES.FR],
  [LANGUAGES.EN]: LANGUAGE_LABELS[LANGUAGES.EN],
  [LANGUAGES.DE]: LANGUAGE_LABELS[LANGUAGES.DE],
  [LANGUAGES.ES]: LANGUAGE_LABELS[LANGUAGES.ES],
  [LANGUAGES.IT]: LANGUAGE_LABELS[LANGUAGES.IT],
  pt: 'Português',
  [LANGUAGES.PT_BR]: LANGUAGE_LABELS[LANGUAGES.PT_BR],
  el: 'ελληνικά',
  sv: 'Svenska',
  ru: 'Русский',
  sl: 'Slovenščina',
  lt: 'Lietuvių',
  tr: 'Türkçe',
  zh: '中文',
  hr: 'Hrvatski',
} as const satisfies Record<StardekkLanguageCode, string>;

const STARDEKK_LANGUAGES_LIST: Language[] = Object.entries(
  STARDEKK_LANGUAGE_LABELS,
).map(([id, label]) => ({ id, label }));

function normalizeApiLanguageCode(apiCode: string | undefined): string {
  if (!apiCode) {
    return '';
  }
  const normalized = apiCode.toLowerCase();
  if (Object.hasOwn(STARDEKK_LANGUAGE_LABELS, normalized)) {
    return normalized;
  }
  // Locale-specific codes like `en-GB`, `pt-PT` fall back to their base code.
  const base = normalized.split('-')[0] ?? '';
  return Object.hasOwn(STARDEKK_LANGUAGE_LABELS, base) ? base : '';
}

function getUserLanguage(
  locale: string | undefined,
  languagesList: Language[],
): Language {
  return languagesList.find((lang) => lang.id === locale) ?? DEFAULT_LANGUAGE;
}

interface SettingsMyHotelCardBusinessProfileSignature {
  Args: {
    isLoading: boolean;
    onPendingLogoChange: (value: 'upload' | 'delete' | null) => void;
    pendingLogoChange: 'upload' | 'delete' | null;
    profile: SubscriptionProfile | null | undefined;
  };
}

export default class SettingsMyHotelCardBusinessProfile extends Component<SettingsMyHotelCardBusinessProfileSignature> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: IntlService;
  @service declare store: Services['store'];
  @service('subscription-active-features')
  declare subscriptionActiveFeatures: Services['subscription-active-features'];
  @service declare toast: Services['toast'];

  @tracked hasAttemptedSave = false;
  @tracked pendingLogoFile: File | null = null;
  // Phone warning state: we use a plain PrismInput instead of PhoneNumberInput because
  // Stardekk-migrated data contains non-normalised values (annotations like "+32 472 (Katia)",
  // multiple numbers in one field, email addresses, free text, etc.) that PhoneNumberInput
  // cannot handle. The warning is non-blocking — invalid numbers can still be saved.
  // phoneIsFocused: suppresses the warning while the user is actively typing.
  // phoneIsTouched: set on blur to reveal the warning; cleared on save success and cancel.
  @tracked phoneIsFocused = false;
  @tracked phoneIsTouched = false;

  // Drives both dirty-state detection and the PATCH body for this card.
  // Scoped so saving here doesn't touch address/lat/lng owned by CardAddress.
  private static readonly OWNED_ATTRS: readonly (keyof SubscriptionProfileOwnedAttrs)[] =
    ['email', 'hotelName', 'language', 'telnr', 'brandUrl'];

  deleteLogoTask = task(async (subscriptionId: string): Promise<void> => {
    await this.ajax.request(this.logoUploadUrl(subscriptionId), {
      method: 'DELETE',
    });
    this.store.push({
      data: {
        id: subscriptionId,
        type: 'subscription/profile',
        attributes: { logoPath: '' },
      },
    });
  });

  saveTask = task({ drop: true }, async (): Promise<void> => {
    this.hasAttemptedSave = true;

    if (!this.hasChanges || this.args.isLoading) {
      return;
    }
    if (this.hasValidationErrors) {
      return;
    }

    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile.hotelName = profile.hotelName.trim();
    profile.email = profile.email.map((e) => e.trim()).filter(Boolean);
    profile.brandUrl = profile.brandUrl.trim();

    try {
      if (this.pendingLogoFile) {
        await this.uploadLogoTask.perform(profile.id!, this.pendingLogoFile);
      } else if (this.args.pendingLogoChange === 'delete') {
        await this.deleteLogoTask.perform(profile.id!);
      }

      // `logoPath` is not in OWNED_ATTRS and is excluded from the PATCH body.
      // The logo endpoints above are the source of truth; the PATCH response
      // echoes the canonical logo_path back into the store via pushAndReturnPayload.
      await profile.saveForSubscription(
        profile.id!,
        SettingsMyHotelCardBusinessProfile.OWNED_ATTRS,
      );

      this.pendingLogoFile = null;
      this.args.onPendingLogoChange(null);
      this.toast.success(
        this.intl.t('settings.my_hotel.hotel_details.save_success'),
      );
      this.hasAttemptedSave = false;
      // Reset touch state so the warning is hidden after a successful save.
      // Product confirmed this is intentional: invalid numbers can be saved deliberately.
      this.phoneIsTouched = false;
    } catch (error) {
      await this.alerting.showErrorAlert();
      this.errorTracking.captureException(error);
    }
  });

  uploadLogoTask = task(
    async (subscriptionId: string, file: File): Promise<void> => {
      const formData = new FormData();
      formData.append('logo', file);
      await this.ajax.request(this.logoUploadUrl(subscriptionId), {
        method: 'PUT',
        data: formData,
        contentType: false,
        processData: false,
        mimeType: 'multipart/form-data',
      });
    },
  );

  // UNIFIED SETTINGS TODO: [INDU-1352] Check the need of this getter: Maybe not required
  //                        duplicated from apps/lumen/app/components/settings/personal/forms/form-user-edit.gts
  get availableLanguages(): Language[] {
    return STARDEKK_LANGUAGES_LIST;
  }

  get emailErrorMessage(): string | undefined {
    if (!this.isEmailValid) {
      return this.intl.t('settings.my_hotel.contact_details.email_invalid');
    }

    return undefined;
  }

  get hasChanges(): boolean {
    if (this.args.pendingLogoChange !== null) {
      return true;
    }
    return (
      this.args.profile?.hasValuesChanges(
        SettingsMyHotelCardBusinessProfile.OWNED_ATTRS,
      ) ?? false
    );
  }

  get hasHotelNameError(): boolean {
    return !this.isHotelNameValid;
  }

  get hasValidationErrors(): boolean {
    return !this.isHotelNameValid || !this.isEmailValid || !this.isWebsiteValid;
  }

  get hotelNameErrorMessage(): string | undefined {
    if (!this.isHotelNameValid) {
      return this.intl.t('general.required');
    }
    return undefined;
  }

  get isEmailValid(): boolean {
    const email = (this.args.profile?.email ?? [])
      .map((e) => e.trim())
      .filter(Boolean);
    return email.length === 0 || email.every(isValidEmailAddress);
  }

  get isHotelNameValid(): boolean {
    if (
      !this.subscriptionActiveFeatures.hasFeature(
        GENERAL_PROPERTY_FEATURES.HOTEL_NAME,
      )
    ) {
      return true;
    }
    return (this.args.profile?.hotelName ?? '').trim().length > 0;
  }

  get isReadOnly(): boolean {
    return (
      this.access.hasUnifiedSettingsChannelDistributionView &&
      !this.access.hasUnifiedSettingsChannelDistributionEdit
    );
  }

  get isWebsiteValid(): boolean {
    const brandUrl = this.args.profile?.brandUrl ?? '';

    return (
      brandUrl === '' || (brandUrl.length <= 255 && URL_REGEX.test(brandUrl))
    );
  }

  get joinedEmails(): string {
    return (this.args.profile?.email ?? []).join(';');
  }

  get pendingLogoFileName(): string {
    return this.pendingLogoFile?.name ?? '';
  }

  get phoneWarningState(): { hasWarnings: boolean; warningMessage: string } {
    // Warning is suppressed while focused (user is still typing) and until
    // the field has been blurred at least once or a save has been attempted.
    if (
      this.phoneIsFocused ||
      (!this.phoneIsTouched && !this.hasAttemptedSave)
    ) {
      return { hasWarnings: false, warningMessage: '' };
    }
    const telnr = this.args.profile?.telnr ?? '';
    if (telnr === '' || isPossiblePhoneNumber(telnr)) {
      return { hasWarnings: false, warningMessage: '' };
    }
    return {
      hasWarnings: true,
      warningMessage: this.intl.t(
        'settings.my_hotel.contact_details.phone.invalid_phone_number',
      ),
    };
  }

  get selectedLanguage(): Language {
    return getUserLanguage(
      normalizeApiLanguageCode(this.args.profile?.language),
      this.availableLanguages,
    );
  }

  get websiteErrorMessage(): string | undefined {
    if (!this.isWebsiteValid) {
      return this.intl.t('settings.my_hotel.contact_details.website_invalid');
    }

    return undefined;
  }

  private logoUploadUrl(subscriptionId: string): string {
    return formatUrl(
      `/${APIGATEWAY}/configure/subscription/profile/logo/`,
      { subscription_id: subscriptionId },
      this.ajax.host,
    );
  }

  @action async cancel(): Promise<void> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('general.unsaved_changes.title'),
      text: this.intl.t('general.unsaved_changes.description'),
      buttons: {
        cancel: {
          text: this.intl.t('general.keep_editing'),
        },
        confirm: {
          text: this.intl.t('general.discard_changes'),
        },
      },
    });

    if (confirmed) {
      const { profile } = this.args;
      if (profile) {
        const changed = profile.changedAttributes();
        for (const attr of SettingsMyHotelCardBusinessProfile.OWNED_ATTRS) {
          if (attr in changed) {
            // Cast needed to set EmberData attrs dynamically without per-attr type guards.
            (profile as unknown as Record<string, unknown>)[attr] =
              changed[attr]![0];
          }
        }
      }

      this.pendingLogoFile = null;
      this.args.onPendingLogoChange(null);
      this.hasAttemptedSave = false;
      // Reset phone warning state on cancel so the warning doesn't persist
      // after the user discards their changes.
      this.phoneIsFocused = false;
      this.phoneIsTouched = false;
    }
  }

  @action handleCancelPendingLogoFile(): void {
    this.pendingLogoFile = null;
    this.args.onPendingLogoChange(null);
  }

  @action handleLogoFileSelect(file: File): void {
    this.pendingLogoFile = file;
    this.args.onPendingLogoChange('upload');
  }

  @action handleStagePendingLogoDelete(): void {
    this.pendingLogoFile = null;
    this.args.onPendingLogoChange('delete');
  }

  @action markPhoneFocused(): void {
    // Suppresses the warning while the user is editing
    this.phoneIsFocused = true;
  }

  @action markPhoneTouched(): void {
    this.phoneIsFocused = false;
    this.phoneIsTouched = true;
  }

  @action setEmailsField(event: Event): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }
    const raw = (event.target as HTMLInputElement).value;
    const normalized = raw.replace(MULTI_EMAIL_REGEX, ';');
    profile.email = normalized.split(';');
  }

  @action setField(field: EditableField, event: Event): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile[field] = (event.target as HTMLInputElement).value;
  }

  @action updateLanguage(language: Language): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile.language = language.id;
  }

  <template>
    {{#if this.isReadOnly}}
      <PrismCard data-test-id="card-business-profile">
        <:header as |Header|>
          <Header.title>
            {{t "settings.my_hotel.hotel_details.business_profile"}}
          </Header.title>
        </:header>

        <:content>
          <div class="grid grid-cols-3 gap-400 pt-400">
            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_NAME
              )
            }}
              <div class="col-span-3 flex">
                <PrismInput
                  @disabled={{true}}
                  @label={{t "settings.my_hotel.hotel_details.hotel_name"}}
                  @removePadding={{true}}
                  @value={{@profile.hotelName}}
                  data-test-id="input-hotel-name"
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_LOGO
              )
            }}
              <div class="col-span-3">
                <LogoField
                  @disabled={{true}}
                  @onCancelPendingFile={{this.handleCancelPendingLogoFile}}
                  @onFileSelect={{this.handleLogoFileSelect}}
                  @onStagePendingDelete={{this.handleStagePendingLogoDelete}}
                  @pendingChange={{@pendingLogoChange}}
                  @pendingFileName={{this.pendingLogoFileName}}
                  @profile={{@profile}}
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_EMAIL
              )
            }}
              <div class="col-span-2 flex self-start">
                <PrismInput
                  @disabled={{true}}
                  @label={{t "settings.my_hotel.contact_details.email"}}
                  @removePadding={{true}}
                  @type="text"
                  @value={{this.joinedEmails}}
                  data-test-id="input-email"
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_PHONE
              )
            }}
              <div class="flex self-start" data-test-id="input-phone">
                <PrismInput
                  @disabled={{true}}
                  @label={{t "settings.my_hotel.contact_details.phone"}}
                  @removePadding={{true}}
                  @value={{@profile.telnr}}
                  data-test-id="phone-number"
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_WEBSITE
              )
            }}
              <div class="col-span-2 flex">
                <PrismInput
                  @disabled={{true}}
                  @label={{t "settings.my_hotel.contact_details.website"}}
                  @removePadding={{true}}
                  @value={{@profile.brandUrl}}
                  data-test-id="input-website"
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_LANGUAGE
              )
            }}
              <div class="grid self-start">
                <label class="input-label text-200--regular">
                  {{t "settings.my_hotel.hotel_details.language"}}
                </label>
                <PrismSelectInput
                  @disabled={{true}}
                  @isSecondary={{true}}
                  @onChange={{this.updateLanguage}}
                  @options={{this.availableLanguages}}
                  @value={{this.selectedLanguage}}
                  data-test-id="input-language"
                />
              </div>
            {{/if}}
          </div>
        </:content>
      </PrismCard>
    {{else}}
      <PrismCard data-test-id="card-business-profile">
        <:header as |Header|>
          <Header.title>
            {{t "settings.my_hotel.hotel_details.business_profile"}}
          </Header.title>
        </:header>

        <:content>
          <div class="grid grid-cols-3 gap-400 pt-400">
            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_NAME
              )
            }}
              <div class="col-span-3 flex">
                <PrismInput
                  @disabled={{@isLoading}}
                  @errorMessage={{this.hotelNameErrorMessage}}
                  @hasErrors={{this.hasHotelNameError}}
                  @isTouched={{this.hasAttemptedSave}}
                  @label={{t "settings.my_hotel.hotel_details.hotel_name"}}
                  @onInput={{fn this.setField "hotelName"}}
                  @removePadding={{true}}
                  @value={{@profile.hotelName}}
                  data-test-id="input-hotel-name"
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_LOGO
              )
            }}
              <div class="col-span-3">
                <LogoField
                  @disabled={{@isLoading}}
                  @onCancelPendingFile={{this.handleCancelPendingLogoFile}}
                  @onFileSelect={{this.handleLogoFileSelect}}
                  @onStagePendingDelete={{this.handleStagePendingLogoDelete}}
                  @pendingChange={{@pendingLogoChange}}
                  @pendingFileName={{this.pendingLogoFileName}}
                  @profile={{@profile}}
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_EMAIL
              )
            }}
              <div class="col-span-2 flex self-start">
                <PrismInput
                  @disabled={{@isLoading}}
                  @errorMessage={{this.emailErrorMessage}}
                  @hasErrors={{not this.isEmailValid}}
                  @isTouched={{this.hasAttemptedSave}}
                  @label={{t "settings.my_hotel.contact_details.email"}}
                  @onInput={{this.setEmailsField}}
                  @removePadding={{true}}
                  @type="text"
                  @value={{this.joinedEmails}}
                  data-test-id="input-email"
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_PHONE
              )
            }}
              <div class="flex self-start" data-test-id="input-phone">
                <PrismInput
                  @disabled={{@isLoading}}
                  @hasWarnings={{this.phoneWarningState.hasWarnings}}
                  @label={{t "settings.my_hotel.contact_details.phone"}}
                  @onBlur={{this.markPhoneTouched}}
                  @onFocusIn={{this.markPhoneFocused}}
                  @onInput={{fn this.setField "telnr"}}
                  @removePadding={{true}}
                  @value={{@profile.telnr}}
                  @warningMessage={{this.phoneWarningState.warningMessage}}
                  data-test-id="phone-number"
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_WEBSITE
              )
            }}
              <div class="col-span-2 flex">
                <PrismInput
                  @disabled={{@isLoading}}
                  @errorMessage={{this.websiteErrorMessage}}
                  @hasErrors={{not this.isWebsiteValid}}
                  @isTouched={{this.hasAttemptedSave}}
                  @label={{t "settings.my_hotel.contact_details.website"}}
                  @onInput={{fn this.setField "brandUrl"}}
                  @removePadding={{true}}
                  @value={{@profile.brandUrl}}
                  data-test-id="input-website"
                />
              </div>
            {{/if}}

            {{#if
              (hasUnifiedSettingsActiveFeature
                GENERAL_PROPERTY_FEATURES.HOTEL_LANGUAGE
              )
            }}
              <div class="grid self-start">
                <label class="input-label text-200--regular">
                  {{t "settings.my_hotel.hotel_details.language"}}
                </label>
                <PrismSelectInput
                  @disabled={{@isLoading}}
                  @isSecondary={{true}}
                  @onChange={{this.updateLanguage}}
                  @options={{this.availableLanguages}}
                  @value={{this.selectedLanguage}}
                  data-test-id="input-language"
                />
              </div>
            {{/if}}
          </div>
        </:content>

        <:footer>
          <div class="flex justify-end gap-200">
            <PrismButton
              @buttonType="secondary"
              @isDisabled={{or
                (not this.hasChanges)
                this.saveTask.isRunning
                @isLoading
              }}
              @label={{t "general.cancel"}}
              @onClick={{this.cancel}}
              data-test-id="button-cancel-business-profile"
            />
            <PrismButton
              @isDisabled={{not this.hasChanges}}
              @isLoading={{or @isLoading this.saveTask.isRunning}}
              @label={{t "general.save"}}
              @onClick={{this.saveTask.perform}}
              data-test-id="button-save-business-profile"
            />
          </div>
        </:footer>
      </PrismCard>
    {{/if}}
  </template>
}
